/**
 * Content bounding box computation for a tessellated {@link GeometrySet}.
 *
 * Used by `MetroMapCanvas` to fit the initial view to the **actual** drawn
 * content rather than to the 4096×4096 canvas box. The London Tube schema
 * content occupies only a landscape-ish subset of the canvas (roughly
 * `~4096 × ~3500`), and centering the square canvas on a portrait phone
 * viewport leaves hundreds of CSS pixels of visible page background above
 * and below the map — see TASK-241 regression screenshots.
 *
 * By scanning every rendered vertex and tracking `(minX, minY, maxX, maxY)`
 * we get the tight content rectangle that the initial zoom/pan should
 * target. Cheap: ~12000 map elements × ~50 vertices on average means
 * ~600k float reads per set, which runs in single-digit milliseconds on
 * any device the map renders on.
 */

import type { GeometrySet } from './types'
import { FLOATS_PER_VERTEX } from './types'

/** Axis-aligned bounding rectangle in canvas coordinate space. */
export interface ContentBBox {
  readonly minX: number
  readonly minY: number
  readonly maxX: number
  readonly maxY: number
}

/**
 * Scans every vertex of every fill/stroke buffer in `set` and returns the
 * tight axis-aligned bounding box around the drawn content. Returns `null`
 * when the set is empty or all buffers are empty — callers should fall
 * back to the canvas-sized box in that case.
 */
export function computeContentBBox(set: GeometrySet): ContentBBox | null {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  let sawVertex = false

  for (const element of set.elements) {
    // Two buffers per element — fill and stroke. Both are interleaved
    // `[x, y, r, g, b, a, x, y, r, g, b, a, ...]` per `FLOATS_PER_VERTEX`.
    const buffers: readonly Float32Array[] = [element.fillVertices, element.strokeVertices]
    for (const buffer of buffers) {
      for (let i = 0; i + 1 < buffer.length; i += FLOATS_PER_VERTEX) {
        const x = buffer[i]
        const y = buffer[i + 1]
        // Defensive skip — a single NaN / Infinity vertex leaked through
        // from a malformed buffer shouldn't poison the entire bbox.
        // Instead we drop that vertex and continue scanning, so the
        // returned box reflects whatever valid vertices exist.
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue
        if (x < minX) minX = x
        if (y < minY) minY = y
        if (x > maxX) maxX = x
        if (y > maxY) maxY = y
        sawVertex = true
      }
    }
  }

  if (!sawVertex) return null
  return { minX, minY, maxX, maxY }
}
